import type { CSSProperties } from 'react';
import { clsx } from 'clsx';

export interface SkeletonProps {
  className?: string;
  width?: number | string;
  height?: number | string;
  /** `text` matches a line of body copy, `block` is a panel, `circle` an avatar. */
  shape?: 'text' | 'block' | 'circle';
  style?: CSSProperties;
}

/**
 * A placeholder with the shape of the thing that is coming.
 *
 * Loading is never a spinner over the layout here — the operator should already be
 * reading the page furniture while the rows arrive, and the page must not jump when
 * they do.
 */
export function Skeleton({ className, width, height, shape = 'text', style }: SkeletonProps) {
  return (
    <span
      aria-hidden
      className={clsx(
        'skeleton block',
        shape === 'text' && 'h-3.5 rounded-sm',
        shape === 'block' && 'rounded-md',
        shape === 'circle' && 'rounded-full',
        className,
      )}
      style={{ width, height, ...style }}
    />
  );
}

/** A few lines of body copy, with a short last line so it reads as a paragraph. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span className={clsx('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={index === lines - 1 ? '60%' : '100%'} />
      ))}
    </span>
  );
}

/** Skeleton rows sized to a real table, so the header does not shift when data lands. */
export function SkeletonRows({
  rows = 8,
  columns = 6,
  density = 'comfortable',
}: {
  rows?: number;
  columns?: number;
  density?: 'comfortable' | 'compact';
}) {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex} className="border-b border-line last:border-0">
          {Array.from({ length: columns }, (_, colIndex) => (
            <td
              key={colIndex}
              className={density === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5'}
            >
              <Skeleton
                width={colIndex === 0 ? '80%' : colIndex % 3 === 0 ? '45%' : '65%'}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
